import { useMemo, useState } from 'react';
import { Box, Typography, Skeleton, Alert, Stack, Button } from '@mui/material';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import TickerCard from './TickerCard';
import DateCard from './DateCard';
import SentimentChart from '@/components/charts/SentimentChart';
import type { ViewpointRow, AggregatedTicker, AggregatedDate, ViewpointEntry, DateRangePreset } from '@/types';

interface ViewpointListProps {
  viewpoints: ViewpointRow[];
  isLoading: boolean;
  error: Error | null;
  mode?: 'date' | 'ticker';
  ticker?: string;  // Required when mode is 'ticker' for ticker page
  channel?: string;  // Required when mode is 'ticker' for channel page
  dateRangePreset?: DateRangePreset;  // For chart grouping in ticker mode
}

// Aggregate viewpoints by ticker
function aggregateByTicker(viewpoints: ViewpointRow[]): AggregatedTicker[] {
  const tickerMap = new Map<string, {
    authors: Set<string>;
    bullAuthors: Set<string>;
    bull: ViewpointEntry[];
    neutral: ViewpointEntry[];
    bear: ViewpointEntry[];
  }>();

  for (const vp of viewpoints) {
    const ticker = vp.ticker;
    if (!tickerMap.has(ticker)) {
      tickerMap.set(ticker, {
        authors: new Set(),
        bullAuthors: new Set(),
        bull: [],
        neutral: [],
        bear: [],
      });
    }

    const data = tickerMap.get(ticker)!;
    const author = vp.channel_name;
    data.authors.add(author);

    const entry: ViewpointEntry = {
      author,
      slot: vp.slot,
      reasoning: vp.reasoning || '',
      references: vp.reference_data || [],
    };

    const sentiment = (vp.sentiment || '').toLowerCase();
    if (/涨|多|bull|up|买/.test(sentiment)) {
      data.bull.push(entry);
      data.bullAuthors.add(author);
    } else if (/跌|空|bear|down|卖/.test(sentiment)) {
      data.bear.push(entry);
    } else {
      // 观望/震荡/中性 or unknown
      data.neutral.push(entry);
    }
  }

  // Convert to array and calculate stats
  const result: AggregatedTicker[] = [];
  for (const [ticker, data] of tickerMap) {
    const authorCount = data.authors.size;
    const bullishRatio = authorCount > 0
      ? Math.round((data.bullAuthors.size / authorCount) * 100)
      : 0;

    result.push({
      ticker,
      authorCount,
      bullAuthors: data.bullAuthors,
      bullishRatio,
      bull: data.bull,
      neutral: data.neutral,
      bear: data.bear,
    });
  }

  // Sort by author count descending
  result.sort((a, b) => b.authorCount - a.authorCount);

  return result;
}

// Aggregate viewpoints by date (for ticker page and channel page)
function aggregateByDate(viewpoints: ViewpointRow[]): AggregatedDate[] {
  const dateMap = new Map<string, {
    authors: Set<string>;
    bullAuthors: Set<string>;
    tickers: Set<string>;
    bullTickers: Set<string>;
    bull: ViewpointEntry[];
    neutral: ViewpointEntry[];
    bear: ViewpointEntry[];
  }>();

  for (const vp of viewpoints) {
    const date = vp.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, {
        authors: new Set(),
        bullAuthors: new Set(),
        tickers: new Set(),
        bullTickers: new Set(),
        bull: [],
        neutral: [],
        bear: [],
      });
    }

    const data = dateMap.get(date)!;
    const author = vp.channel_name;
    const ticker = vp.ticker;
    data.authors.add(author);
    data.tickers.add(ticker);

    const entry: ViewpointEntry = {
      author,
      ticker,
      slot: vp.slot,
      reasoning: vp.reasoning || '',
      references: vp.reference_data || [],
    };

    const sentiment = (vp.sentiment || '').toLowerCase();
    if (/涨|多|bull|up|买/.test(sentiment)) {
      data.bull.push(entry);
      data.bullAuthors.add(author);
      data.bullTickers.add(ticker);
    } else if (/跌|空|bear|down|卖/.test(sentiment)) {
      data.bear.push(entry);
    } else {
      data.neutral.push(entry);
    }
  }

  // Convert to array and calculate stats
  const result: AggregatedDate[] = [];
  for (const [date, data] of dateMap) {
    const authorCount = data.authors.size;
    const bullishRatio = authorCount > 0
      ? Math.round((data.bullAuthors.size / authorCount) * 100)
      : 0;
    const tickerCount = data.tickers.size;
    const tickerBullishRatio = tickerCount > 0
      ? Math.round((data.bullTickers.size / tickerCount) * 100)
      : 0;

    result.push({
      date,
      authorCount,
      bullAuthors: data.bullAuthors,
      bullishRatio,
      tickerCount,
      bullTickers: data.bullTickers,
      tickerBullishRatio,
      bull: data.bull,
      neutral: data.neutral,
      bear: data.bear,
    });
  }

  // Sort by date descending (newest first)
  result.sort((a, b) => b.date.localeCompare(a.date));

  return result;
}

export default function ViewpointList({ viewpoints, isLoading, error, mode = 'date', ticker, channel, dateRangePreset = 'week' }: ViewpointListProps) {
  // Detect if this is a channel page (has channel but no ticker)
  const isChannelPage = !!channel && !ticker;

  // Aggregate viewpoints by ticker (for date mode)
  const aggregatedTickers = useMemo(
    () => mode === 'date' ? aggregateByTicker(viewpoints) : [],
    [viewpoints, mode]
  );

  // Aggregate viewpoints by date (for ticker mode)
  const aggregatedDates = useMemo(
    () => mode === 'ticker' ? aggregateByDate(viewpoints) : [],
    [viewpoints, mode]
  );

  // Global toggle states - version number increments to trigger child updates
  const [cardExpandVersion, setCardExpandVersion] = useState(0);
  const [sentimentExpandVersion, setSentimentExpandVersion] = useState(0);
  const [cardsCollapsed, setCardsCollapsed] = useState(false);
  const [sentimentsCollapsed, setSentimentsCollapsed] = useState(false);

  const handleToggleCards = () => {
    setCardsCollapsed(!cardsCollapsed);
    setCardExpandVersion(v => v + 1);
  };

  const handleToggleSentiments = () => {
    setSentimentsCollapsed(!sentimentsCollapsed);
    setSentimentExpandVersion(v => v + 1);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        加载失败，请联系 @AlphaMoeMoe
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={200} />
        ))}
      </Stack>
    );
  }

  if (viewpoints.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">
          没有找到符合条件的观点，请调整筛选条件
        </Typography>
      </Box>
    );
  }

  // Summary text based on mode
  const summaryText = mode === 'ticker'
    ? `共 ${aggregatedDates.length} 天，${viewpoints.length} 条观点`
    : `共 ${aggregatedTickers.length} 个标的，${viewpoints.length} 条观点`;

  // Card toggle button text based on mode
  const cardToggleText = mode === 'ticker'
    ? (cardsCollapsed ? '展开日期' : '折叠日期')
    : (cardsCollapsed ? '展开标的' : '折叠标的');

  return (
    <>
      {/* Sentiment Chart for ticker mode */}
      {mode === 'ticker' && aggregatedDates.length > 0 && (
        <SentimentChart data={aggregatedDates} preset={dateRangePreset} countBy={isChannelPage ? 'ticker' : 'channel'} />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {summaryText}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={sentimentsCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
            onClick={handleToggleSentiments}
            sx={{ fontSize: '0.75rem', py: 0.5 }}
          >
            {sentimentsCollapsed ? '展开观点' : '折叠观点'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={cardsCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
            onClick={handleToggleCards}
            sx={{ fontSize: '0.75rem', py: 0.5 }}
          >
            {cardToggleText}
          </Button>
        </Box>
      </Box>
      <Stack spacing={2}>
        {mode === 'ticker' ? (
          // Ticker mode: group by date
          aggregatedDates.map((dateData) => (
            <DateCard
              key={dateData.date}
              data={dateData}
              ticker={ticker || ''}
              isChannelPage={isChannelPage}
              forceDateExpanded={!cardsCollapsed}
              dateExpandVersion={cardExpandVersion}
              forceSentimentExpanded={!sentimentsCollapsed}
              sentimentExpandVersion={sentimentExpandVersion}
            />
          ))
        ) : (
          // Date mode: group by ticker
          aggregatedTickers.map((tickerData) => (
            <TickerCard
              key={tickerData.ticker}
              data={tickerData}
              forceTickerExpanded={!cardsCollapsed}
              tickerExpandVersion={cardExpandVersion}
              forceSentimentExpanded={!sentimentsCollapsed}
              sentimentExpandVersion={sentimentExpandVersion}
              disableTickerLink={false}
            />
          ))
        )}
      </Stack>
    </>
  );
}
